import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import FacilitatorJourneyDetail from './FacilitatorJourneyDetail';
import ProfilePage from './ProfilePage';
import ProfileDropdown from './ProfileDropdown';
import '../FacilitatorDashboard.css';

const API_URL = 'http://localhost:5000';
const ITEMS_PER_PAGE = 12;

function Dashboard({ token, userId, userName, userRole, onLogout }) {
  const [journeys, setJourneys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedJourney, setSelectedJourney] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortBy, setSortBy] = useState('updated');
  const [currentPage, setCurrentPage] = useState(1);
  const [showProfile, setShowProfile] = useState(false);

  const fetchDashboardData = useCallback(async () => {
    try {
      setLoading(true);
      setError('');

      // Fetch journeys (endpoint is now role-aware)
      const journeysRes = await axios.get(`${API_URL}/api/journeys`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      setJourneys(journeysRes.data.journeys || []);
      setCurrentPage(1);
    } catch (err) {
      console.error('Fetch dashboard data error:', err);
      setError('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  }, [token, userId]);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData, userRole]);

  const getFilteredAndSortedJourneys = () => {
    let filtered = [...journeys];

    // Filter by search term (title match)
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(journey =>
        journey.title.toLowerCase().includes(term)
      );
    }

    // Filter by status
    if (statusFilter !== 'all') {
      filtered = filtered.filter(journey => journey.status === statusFilter);
    }

    // Sort
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'name':
          return a.title.localeCompare(b.title);
        case 'updated':
          return new Date(b.updated_at || b.created_at) - new Date(a.updated_at || a.created_at);
        case 'created':
          return new Date(b.created_at) - new Date(a.created_at);
        default:
          return 0;
      }
    });

    // Calculate pagination
    const totalPages = Math.ceil(filtered.length / ITEMS_PER_PAGE);
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    const paginatedResults = filtered.slice(startIndex, startIndex + ITEMS_PER_PAGE);

    return {
      filtered,
      paginatedResults,
      totalPages,
      totalCount: filtered.length,
    };
  };

  const getDashboardTitle = () => {
    if (userRole === 'facilitator') return 'Facilitator Dashboard';
    if (userRole === 'contributor') return 'My Journeys';
    return 'Dashboard';
  };

  const getDashboardSubtitle = () => {
    if (userRole === 'facilitator') return 'Manage your innovation journeys';
    if (userRole === 'contributor') return 'View journeys you are part of';
    return '';
  };

  if (loading) {
    return (
      <div className="facilitator-dashboard">
        <div className="loading">Loading dashboard...</div>
      </div>
    );
  }

  // Show journey detail view if a journey is selected
  if (selectedJourney) {
    const journey = journeys.find(j => j.id === selectedJourney);
    if (journey) {
      return (
        <FacilitatorJourneyDetail
          journey={journey}
          token={token}
          userRole={userRole}
          onBack={() => {
            setSelectedJourney(null);
            fetchDashboardData();
          }}
          onTeamUpdate={fetchDashboardData}
        />
      );
    }
  }

  if (showProfile) {
    return (
      <ProfilePage
        token={token}
        onBack={() => setShowProfile(false)}
      />
    );
  }

  return (
    <div className="facilitator-dashboard">
      <div className="dashboard-header">
        <div>
          <h1>{getDashboardTitle()}</h1>
          <p className="subtitle">{getDashboardSubtitle()}</p>
        </div>
        <ProfileDropdown
          userName={userName}
          userRole={userRole}
          onLogout={onLogout}
          onNavigateToProfile={() => setShowProfile(true)}
        />
      </div>

      {error && <div className="error-banner">{error}</div>}

      {/* Journeys Overview */}
      <div className="dashboard-section">
        <h2>My Journeys</h2>
        {journeys.length === 0 ? (
          <div className="empty-state">
            <p>
              {userRole === 'contributor'
                ? 'No journeys assigned yet. Contact an administrator to be added to a journey.'
                : 'No journeys yet'}
            </p>
          </div>
        ) : (
          <>
            {/* Search, Filter, Sort Bar */}
            <div className="search-filter-bar">
              <div className="search-input-wrapper">
                <input
                  type="text"
                  placeholder="Search journeys by title..."
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setCurrentPage(1);
                  }}
                />
              </div>

              {/* Filter Pills */}
              <div className="filter-pills">
                <label>Status:</label>
                <button
                  className={`filter-pill ${statusFilter === 'all' ? 'active' : ''}`}
                  onClick={() => {
                    setStatusFilter('all');
                    setCurrentPage(1);
                  }}
                >
                  All
                </button>
                <button
                  className={`filter-pill ${statusFilter === 'active' ? 'active' : ''}`}
                  onClick={() => {
                    setStatusFilter('active');
                    setCurrentPage(1);
                  }}
                >
                  Active
                </button>
                <button
                  className={`filter-pill ${statusFilter === 'paused' ? 'active' : ''}`}
                  onClick={() => {
                    setStatusFilter('paused');
                    setCurrentPage(1);
                  }}
                >
                  Paused
                </button>
              </div>

              {/* Sort and Results Counter */}
              <div className="sort-results-container">
                <div className="sort-select-wrapper">
                  <label htmlFor="sort-select">Sort by:</label>
                  <select
                    id="sort-select"
                    className="sort-select"
                    value={sortBy}
                    onChange={(e) => {
                      setSortBy(e.target.value);
                      setCurrentPage(1);
                    }}
                  >
                    <option value="updated">Recently Updated</option>
                    <option value="created">Recently Created</option>
                    <option value="name">Name (A-Z)</option>
                  </select>
                </div>
                <div className="results-counter">
                  {(() => {
                    const { totalCount, paginatedResults } = getFilteredAndSortedJourneys();
                    return `Showing ${paginatedResults.length > 0 ? (currentPage - 1) * ITEMS_PER_PAGE + 1 : 0} - ${(currentPage - 1) * ITEMS_PER_PAGE + paginatedResults.length} of ${totalCount}`;
                  })()}
                </div>
              </div>
            </div>

            {/* Journeys Grid */}
            {(() => {
              const { paginatedResults, totalPages, totalCount } = getFilteredAndSortedJourneys();
              return (
                <>
                  {totalCount === 0 ? (
                    <div className="empty-state">
                      <p>No journeys match your search criteria</p>
                    </div>
                  ) : (
                    <>
                      <div className="journeys-grid">
                        {paginatedResults.map(journey => (
              <div
                key={journey.id}
                className="journey-card"
                onClick={() => setSelectedJourney(journey.id)}
              >
                <div className="journey-card-header">
                  <h3>{journey.title}</h3>
                  <span className={`status-badge ${journey.status}`}>
                    {journey.status.toUpperCase()}
                  </span>
                </div>

                {journey.description && (
                  <p className="journey-description">{journey.description}</p>
                )}

                <div className="journey-stats">
                  <div className="journey-stat">
                    <span className="icon">👥</span>
                    <span>{journey.team_count} Members</span>
                  </div>
                  <div className="journey-stat">
                    <span className="icon">💡</span>
                    <span>{journey.ideas_count} Ideas</span>
                  </div>
                  <div className="journey-stat">
                    <span className="icon">📍</span>
                    <span>{journey.current_stage}</span>
                  </div>
                </div>
              </div>
                        ))}
                      </div>

                      {/* Pagination */}
                      {totalPages > 1 && (
                        <div className="pagination">
                          <button
                            className="pagination-button"
                            onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                            disabled={currentPage === 1}
                          >
                            ← Previous
                          </button>
                          <div className="pagination-info">
                            Page {currentPage} of {totalPages}
                          </div>
                          <button
                            className="pagination-button"
                            onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                            disabled={currentPage === totalPages}
                          >
                            Next →
                          </button>
                        </div>
                      )}
                    </>
                  )}
                </>
              );
            })()}
          </>
        )}
      </div>
    </div>
  );
}

export default Dashboard;
